import classNames from "classnames";
import { Button } from "PFComponents/button";
import Calendar from "PFComponents/calendar/calendar";
import InputFieldSet from "PFComponents/text/input_field_set";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import CalendarIcon from "PFIcons/calendar.svg";
import FilterCleanIcon from "PFIcons/filter-clean.svg";
import HistoryIcon from "PFIcons/history.svg";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { usePopper } from "react-popper";

import css from "./inline_calendar.module.scss";

const InlineCalendar = ({
  style,
  rootCalendarClassName,
  letClear,
  onRestore,
  handleChange,
  showTime,
  displayValue,
  selectedDate,
  label,
  locked,
  minDate,
  maxDate,
  lockedTip,
  disabled,
  qaId,
  error,
  whiteStyle,
  placeholder,
  required,
  calendarProps,
  calendarStyle,
  popperOptions,
  shouldCloseOnSelection,
  portalRef,
  clearDate
}) => {
  const [selectedDay, setSelectedDay] = useState(selectedDate);
  const [display, setDisplay] = useState(false);

  const popperReferenceElement = useRef();
  const [popperElement, setPopperElement] = useState(null);
  const { dateFormatString, dateTimeFormatString, formatISODateTime, utc } = useDateFormatter();

  const menuRef = useRef();

  useEffect(() => {
    setSelectedDay(selectedDate);
  }, [selectedDate]);

  const renderValue = () => {
    if (displayValue) {
      return displayValue;
    } else {
      const fmt = showTime ? dateTimeFormatString : dateFormatString;
      return selectedDate ? utc(selectedDate).format(fmt) : "";
    }
  };

  const stopPropagation = (event) => {
    event.stopPropagation();
  };

  const handleCalendarChange = (date) => {
    if (shouldCloseOnSelection) {
      setSelectedDay(date);
      setDisplay(false);
    }

    handleChange && handleChange(date);
  };

  const handleInlineFocus = () => {
    setDisplay(true);
  };

  useEffect(() => {
    const handleClick = (event) => {
      if (portalRef) {
        if (menuRef.current && menuRef.current.contains(event.target)) {
          return;
        }
        if (portalRef.current && !portalRef.current.contains(event.target)) {
          setDisplay(false);
        }
      } else {
        if (popperReferenceElement?.current && !popperReferenceElement.current?.contains(event.target)) {
          setDisplay(false);
        }
      }
    };

    document.addEventListener("click", handleClick, true);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, []);

  const wrapperClass = rootCalendarClassName ? null : css.inline;
  const value = renderValue();

  const { styles, attributes } = usePopper(popperReferenceElement.current, popperElement, {
    placement: "bottom",
    ...popperOptions
  });

  const CalendarRenderer = (
    <div
      className={css.calendar}
      ref={setPopperElement}
      style={{ ...calendarStyle, ...styles.popper, zIndex: 20000 }}
      {...attributes.popper}
    >
      <Calendar
        {...calendarProps}
        minDate={minDate}
        maxDate={maxDate}
        showTime={showTime}
        rootClassName={rootCalendarClassName}
        selectedDate={selectedDay}
        handleChange={handleCalendarChange}
        onMouseDown={stopPropagation}
      />
    </div>
  );

  return (
    <div className={classNames(css.root, wrapperClass)} style={style} ref={popperReferenceElement}>
      <div className={css.actions}>
        {onRestore && (
          <Button kind="text" onClick={onRestore}>
            <HistoryIcon height={16} width={16} />
          </Button>
        )}
        {value && letClear && (
          <Button kind="text" onClick={() => handleChange(clearDate ? clearDate : formatISODateTime())}>
            <FilterCleanIcon height={16} width={16} />
          </Button>
        )}
      </div>
      <div ref={menuRef}>
        <InputFieldSet
          icon={<CalendarIcon width={20} height={20} className={css.calendarIcon} />}
          label={label}
          name={name}
          value={value}
          controlledValue
          locked={locked}
          lockedTip={lockedTip}
          disabled={disabled}
          qaId={qaId}
          error={error}
          whiteStyle={whiteStyle}
          handleFocus={handleInlineFocus}
          onMouseDown={stopPropagation}
          placeholder={placeholder}
          required={required}
          inputStyle={{ paddingRight: 30, minHeight: 40, height: 40 }}
          simplifiedAutoSizeLogic
          handleKeyDown={(event) => {
            if (event.keyCode === 13) {
              event.preventDefault(); // avoid accidentaly adding a newline to the input
            }
          }}
        />
        {display &&
          !disabled &&
          (portalRef?.current
            ? ReactDOM.createPortal(CalendarRenderer, portalRef.current)
            : CalendarRenderer)}
      </div>
    </div>
  );
};

InlineCalendar.propTypes = {
  ...InputFieldSet.propTypes,
  ...Calendar.propTypes,
  style: PropTypes.object,
  shouldCloseOnSelection: PropTypes.bool,

  handleChange: PropTypes.func.isRequired,
  saveRef: PropTypes.func,
  letClear: PropTypes.bool,
  onRestore: PropTypes.func,
  portalRef: PropTypes.string,
  clearDate: PropTypes.string,
  rootCalendarClassName: PropTypes.string,
  calendarProps: PropTypes.object,
  popperOptions: PropTypes.object
};

InlineCalendar.defaultProps = {
  ...Calendar.defaultProps,
  style: null,
  shouldCloseOnSelection: true,
  letClear: false,
  portalRef: null,
  clearDate: null
};

export default InlineCalendar;
